import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, EyeIcon, Loader2Icon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { FormPreview } from '../components/FormPreview';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { FormBuilder } from '../components/builder/FormBuilder';
import { FormBlock, FormTheme, FormBuilderSchema, DEFAULT_FORM_THEME } from '../components/builder/types';
import { formTemplatesLibraryApi, FormTemplateRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
const CATEGORIES = ['Newsletter', 'Contact', 'Event', 'Registration', 'Feedback', 'Other'];
// Welcome screen config interface (matches SignupFormEditor)
interface WelcomeScreenConfig {
  enabled: boolean;
  title: string;
  description: string;
  buttonText: string;
  imageUrl: string;
  estimatedTime: string;
  backgroundColor: string;
  textColor: string;
}
export function FormTemplateLibraryEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  // FIX: Team is always the workspace name, Region is the sub-region
  const team = currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const region = currentSubRegion || '';
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showBuilderConfirm, setShowBuilderConfirm] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'js'>('html');
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [formData, setFormData] = useState({
    html: '',
    css: '',
    js: ''
  });
  const [builderBlocks, setBuilderBlocks] = useState<FormBlock[]>([]);
  const [builderTheme, setBuilderTheme] = useState<FormTheme>(DEFAULT_FORM_THEME);
  // Welcome screen state
  const [welcomeScreenEnabled, setWelcomeScreenEnabled] = useState(false);
  const [welcomeTitle, setWelcomeTitle] = useState('');
  const [welcomeDescription, setWelcomeDescription] = useState('');
  const [welcomeButtonText, setWelcomeButtonText] = useState('Join Us');
  const [welcomeImageUrl, setWelcomeImageUrl] = useState('');
  const [welcomeEstimatedTime, setWelcomeEstimatedTime] = useState('');
  const [welcomeBgColor, setWelcomeBgColor] = useState('#000000');
  const [welcomeTextColor, setWelcomeTextColor] = useState('#ffffff');
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      formTemplatesLibraryApi.get(id).then(record => {
        if (record) {
          setTemplateName(record.Template_Name || '');
          setDescription(record.Description || '');
          setCategory(record.Category || '');
          setFormData({
            html: record.HTML || '',
            css: record.CSS || '',
            js: record.JavaScript1 || ''
          });
          if (record.Builder_Mode === 'builder' && record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema & {
                welcomeScreen?: WelcomeScreenConfig;
              } = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
              setEditorMode('builder');
              // Restore welcome screen config
              if (schema.welcomeScreen) {
                setWelcomeScreenEnabled(schema.welcomeScreen.enabled || false);
                setWelcomeTitle(schema.welcomeScreen.title || '');
                setWelcomeDescription(schema.welcomeScreen.description || '');
                setWelcomeButtonText(schema.welcomeScreen.buttonText || 'Join Us');
                setWelcomeImageUrl(schema.welcomeScreen.imageUrl || '');
                setWelcomeEstimatedTime(schema.welcomeScreen.estimatedTime || '');
                setWelcomeBgColor(schema.welcomeScreen.backgroundColor || '#000000');
                setWelcomeTextColor(schema.welcomeScreen.textColor || '#ffffff');
              }
            } catch {
              /* fallback to code */
            }
          } else if (record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema & {
                welcomeScreen?: WelcomeScreenConfig;
              } = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
              // Restore welcome screen config
              if (schema.welcomeScreen) {
                setWelcomeScreenEnabled(schema.welcomeScreen.enabled || false);
                setWelcomeTitle(schema.welcomeScreen.title || '');
                setWelcomeDescription(schema.welcomeScreen.description || '');
                setWelcomeButtonText(schema.welcomeScreen.buttonText || 'Join Us');
                setWelcomeImageUrl(schema.welcomeScreen.imageUrl || '');
                setWelcomeEstimatedTime(schema.welcomeScreen.estimatedTime || '');
                setWelcomeBgColor(schema.welcomeScreen.backgroundColor || '#000000');
                setWelcomeTextColor(schema.welcomeScreen.textColor || '#ffffff');
              }
            } catch {
              /* ignore */
            }
          }
        } else {
          toast.error('Template not found');
          navigate('/form-templates');
        }
      }).catch(err => {
        toast.error('Failed to load template', {
          description: err instanceof Error ? err.message : 'Unknown error'
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Template Name is required');
      return;
    }
    setIsSaving(true);
    // Build welcome screen config
    const welcomeConfig: WelcomeScreenConfig = {
      enabled: welcomeScreenEnabled,
      title: welcomeTitle,
      description: welcomeDescription,
      buttonText: welcomeButtonText,
      imageUrl: welcomeImageUrl,
      estimatedTime: welcomeEstimatedTime,
      backgroundColor: welcomeBgColor,
      textColor: welcomeTextColor
    };
    const payload: Record<string, any> = {
      Template_Name: templateName,
      Description: description,
      Category: category,
      HTML: formData.html,
      CSS: formData.css,
      JavaScript1: formData.js,
      Builder_Mode: editorMode,
      Team: team,
      Region: region
    };
    // Always save Builder_Schema to persist welcome screen config
    const schemaData: Record<string, any> = {
      welcomeScreen: welcomeConfig
    };
    if (builderBlocks.length > 0 || editorMode === 'builder') {
      schemaData.blocks = builderBlocks;
      schemaData.theme = builderTheme;
    }
    payload.Builder_Schema = JSON.stringify(schemaData);
    try {
      if (isEditing && id) {
        await formTemplatesLibraryApi.update(id, payload);
        toast.success('Form template updated');
      } else {
        await formTemplatesLibraryApi.create(payload);
        toast.success('Form template created');
        navigate('/form-templates');
      }
    } catch (err) {
      toast.error('Failed to save template', {
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setIsSaving(false);
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4395">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4396" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4397">Loading template...</span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-4398">
      <div className="flex items-center justify-between" data-id="element-4399">
        <div className="flex items-center gap-4" data-id="element-4400">
          <Link to="/form-templates" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-4401">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-4402" />
          </Link>
          <div data-id="element-4403">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4404">
              {isEditing ? 'Edit Form Template' : 'Create Form Template'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-4405">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your template.' : 'Write HTML, CSS, and JS for this template.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-4406">
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-4407">
            <button onClick={() => setEditorMode('code')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4408">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && formData.html && builderBlocks.length === 0) {
              setShowBuilderConfirm(true);
              return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4409">
              Builder
            </button>
          </div>
          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-4410">
            <EyeIcon className="h-4 w-4" data-id="element-4411" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-4412">
            {isSaving ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-4413" /> : <SaveIcon className="h-4 w-4" data-id="element-4414" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {/* Welcome Screen — Full-width configuration section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-4415">
        <div className="px-5 py-3.5 flex items-center justify-between border-b border-gray-100 bg-gray-50/60" data-id="element-4416">
          <div className="flex items-center gap-3" data-id="element-4417">
            <div className="h-7 w-7 rounded-md bg-indigo-50 flex items-center justify-center" data-id="element-4418">
              <svg className="h-4 w-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-4419">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" data-id="element-4420" />
              </svg>
            </div>
            <div data-id="element-4421">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-4422">
                Welcome Screen
              </h3>
              <p className="text-xs text-gray-500" data-id="element-4423">
                Optional intro screen shown before the form
              </p>
            </div>
          </div>
          <button type="button" onClick={() => setWelcomeScreenEnabled(!welcomeScreenEnabled)} className={`${welcomeScreenEnabled ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`} data-id="element-4424">
            <span className={`${welcomeScreenEnabled ? 'translate-x-4' : 'translate-x-0'} pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} data-id="element-4425" />
          </button>
        </div>

        {welcomeScreenEnabled && <div className="p-5" data-id="element-4426">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4" data-id="element-4427">
              <div className="space-y-1.5" data-id="element-4428">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4429">
                  Title
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeTitle} onChange={e => setWelcomeTitle(e.target.value)} placeholder="Tell Everyone!" data-id="element-4430" />
              </div>
              <div className="space-y-1.5" data-id="element-4431">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4432">
                  Button Text
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeButtonText} onChange={e => setWelcomeButtonText(e.target.value)} placeholder="Join Us" data-id="element-4433" />
              </div>
              <div className="sm:col-span-2 space-y-1.5" data-id="element-4434">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4435">
                  Description
                </label>
                <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeDescription} onChange={e => setWelcomeDescription(e.target.value)} placeholder="The movement is growing every day..." data-id="element-4436" />
              </div>
              <div className="space-y-1.5" data-id="element-4437">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4438">
                  Hero Image URL{' '}
                  <span className="text-gray-400 font-normal" data-id="element-4439">(Optional)</span>
                </label>
                <input type="url" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeImageUrl} onChange={e => setWelcomeImageUrl(e.target.value)} placeholder="https://..." data-id="element-4440" />
              </div>
              <div className="space-y-1.5" data-id="element-4441">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4442">
                  Estimated Time{' '}
                  <span className="text-gray-400 font-normal" data-id="element-4443">(Optional)</span>
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeEstimatedTime} onChange={e => setWelcomeEstimatedTime(e.target.value)} placeholder="2 minutes" data-id="element-4444" />
              </div>
              <div className="space-y-1.5" data-id="element-4445">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4446">
                  Background Color
                </label>
                <div className="flex items-center gap-2" data-id="element-4447">
                  <input type="color" value={welcomeBgColor} onChange={e => setWelcomeBgColor(e.target.value)} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-4448" />
                  <input type="text" value={welcomeBgColor} onChange={e => setWelcomeBgColor(e.target.value)} className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono uppercase" data-id="element-4449" />
                  <div className="h-6 w-12 rounded" style={{
                backgroundColor: welcomeBgColor
              }} data-id="element-4450" />
                </div>
              </div>
              <div className="space-y-1.5" data-id="element-4451">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4452">
                  Text Color
                </label>
                <div className="flex items-center gap-2" data-id="element-4453">
                  <input type="color" value={welcomeTextColor} onChange={e => setWelcomeTextColor(e.target.value)} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-4454" />
                  <input type="text" value={welcomeTextColor} onChange={e => setWelcomeTextColor(e.target.value)} className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono uppercase" data-id="element-4455" />
                  <div className="h-6 w-12 rounded border border-gray-200" style={{
                backgroundColor: welcomeTextColor
              }} data-id="element-4456" />
                </div>
              </div>
            </div>
          </div>}
      </div>

      {editorMode === 'builder' ? <div className="space-y-6" data-id="element-4457">
          <FormBuilder initialBlocks={builderBlocks} initialTheme={builderTheme} onSchemaChange={(blocks, theme) => {
        setBuilderBlocks(blocks);
        setBuilderTheme(theme);
      }} onHtmlChange={(html, css) => {
        setFormData(prev => ({
          ...prev,
          html,
          css
        }));
      }} data-id="element-4458" />
          <div className="grid grid-cols-3 gap-6" data-id="element-4459">
            <div className="col-span-2" data-id="element-4460" />
            <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} region={region} data-id="element-4461" />
          </div>
        </div> : <div className="grid grid-cols-3 gap-6" data-id="element-4462">
          <div className="col-span-2 space-y-6" data-id="element-4463">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-4464">
              <div className="border-b border-gray-200 px-4 py-3 flex items-center gap-4" data-id="element-4465">
                {(['html', 'css', 'js'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${activeTab === tab ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-4466">
                    {tab === 'js' ? 'JavaScript' : tab.toUpperCase()}
                  </button>)}
              </div>
              <div className="p-4" data-id="element-4467">
                {activeTab === 'html' && <CodeEditor label="Form HTML" language="html" placeholder="<form>...</form>" value={formData.html} onChange={e => setFormData({
              ...formData,
              html: e.target.value
            })} data-id="element-4468" />}
                {activeTab === 'css' && <CodeEditor label="Custom CSS" language="css" placeholder=".form { ... }" value={formData.css} onChange={e => setFormData({
              ...formData,
              css: e.target.value
            })} data-id="element-4469" />}
                {activeTab === 'js' && <CodeEditor label="Custom JavaScript" language="javascript" placeholder="// Custom JS..." value={formData.js} onChange={e => setFormData({
              ...formData,
              js: e.target.value
            })} data-id="element-4470" />}
              </div>
            </div>
            <GptCodeAssistant mode="form" currentCode={formData} onCodeGenerated={code => {
          setFormData(prev => ({
            html: code.html || prev.html,
            css: code.css !== undefined ? code.css : prev.css,
            js: code.js !== undefined ? code.js : prev.js
          }));
        }} data-id="element-4471" />
          </div>
          <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} region={region} data-id="element-4472" />
        </div>}

      {showPreview && <PreviewModal html={formData.html} css={formData.css} js={formData.js} onClose={() => setShowPreview(false)} title="Template Preview" data-id="element-4473" />}

      <ConfirmModal isOpen={showBuilderConfirm} title="Switch to Builder" message="The builder starts with a blank canvas — your existing code cannot be imported into the visual builder. However, your code is preserved and will still be there if you switch back to Code mode without saving." confirmLabel="Switch to Builder" variant="warning" onCancel={() => setShowBuilderConfirm(false)} onConfirm={() => {
      setShowBuilderConfirm(false);
      setEditorMode('builder');
    }} data-id="element-4474" />
    </div>;
}
function SettingsPanel({
  templateName,
  setTemplateName,
  description,
  setDescription,
  category,
  setCategory,
  team,
  region
}: {
  templateName: string;
  setTemplateName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  category: string;
  setCategory: (v: string) => void;
  team: string;
  region: string;
}) {
  return <div className="space-y-6" data-id="element-4475">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-4476">
        <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4477">
          Template Settings
        </h3>
        <div className="space-y-2" data-id="element-4478">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4479">
            Template Name
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="My Form Template" data-id="element-4480" />
        </div>
        <div className="space-y-2" data-id="element-4481">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4482">
            Description
          </label>
          <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe what this template is for..." data-id="element-4483" />
        </div>
        <div className="space-y-2" data-id="element-4484">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4485">
            Category
          </label>
          <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={category} onChange={e => setCategory(e.target.value)} data-id="element-4486">
            <option value="" data-id="element-4487">Select category...</option>
            {CATEGORIES.map(c => <option key={c} value={c} data-id="element-4488">
                {c}
              </option>)}
          </select>
        </div>
        <div className="space-y-2" data-id="element-4489">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4490">
            Team
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-4491" />
          <p className="text-xs text-gray-400" data-id="element-4492">
            Automatically set from your current workspace
          </p>
        </div>
        <div className="space-y-2" data-id="element-4493">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4494">
            Region
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={region || '(All regions)'} disabled data-id="element-4495" />
          <p className="text-xs text-gray-400" data-id="element-4496">
            Automatically set from your current scope selection
          </p>
        </div>
      </div>
    </div>;
}